/**
 * ceilingsRef.js — Storalytic benchmark reference values.
 *
 * Single source of truth (JS side) for E_max, C_max, TWT, and TIT
 * benchmark ceilings per (sector × zone_type). Used by the frontend
 * gauge math, classifier, and provenance tooltips.
 *
 * ==================================================================
 * SOURCE OF TRUTH for the ceiling values (JS side).
 * ==================================================================
 *
 * The backend does NOT hand-maintain a copy of these values. They are
 * dumped (together with settings.js) into
 * backend/applicaite/signals/prioritization_config.json, which the
 * backend runtime reads. Change the values here, then regenerate that
 * JSON. (The former backend/applicaite/tools/ceilings_reference.py
 * manual mirror was removed — it was unused and had drifted out of
 * sync, which is exactly the failure mode a hand-maintained copy invites.)
 *
 * Each cell carries provenance:
 *
 *     {
 *         value: 0.75,
 *         range: [0.63, 0.83],      // optional
 *         confidence: 'PR',         // 'PR' | 'CVT' | 'SRS'
 *         source: 'Hui et al. (2013) JMR 50(4):445-462',
 *     }
 *
 * Confidence taxonomy (from Storalytic methodology document §3):
 *   PR  — Peer-Reviewed: direct academic measurement of this metric
 *   CVT — Cross-Vendor Triangulation: multiple commercial sources agree
 *   SRS — Structured Retail-Physics Synthesis: documented derivation
 *         chained from established retail principles + published
 *         store-level ranges
 *
 * Three-level hierarchy:
 *   Level 1: sector default (== Level 2 with zone_type="product")
 *   Level 2: sector × zone_type ("product" | "demo" | "display")
 *   Level 3: per-zone self-calibrated (NOT stored here; computed from
 *           the past_90_days slot and stored in DetectionZoneSettings)
 */

// ---------------------------------------------------------------------------
// Sector list — keep in sync with CEILINGS keys below and segment picker UI.
// ---------------------------------------------------------------------------
export const SECTORS = Object.freeze([
  'grocery',
  'diy',
  'furniture',
  'fashion',
  'discount',
  'pharmacy',
  'specialty',
  'large_format',
])

export const ZONE_TYPES_WITH_CEILINGS = Object.freeze([
  'product',
  'demo',
  'display',
])

// Default fallback sector when none is configured. Matches the legacy
// global ceilings (Emax 0.50, Cmax 0.25), DIY shape.
export const DEFAULT_SECTOR = 'diy'

// ---------------------------------------------------------------------------
// CEILINGS — Level 2 reference table (sector × zone_type → {Emax, Cmax})
//
// Level 1 sector defaults are derived by indexing the "product" zone-type
// slot for the given sector.
// ---------------------------------------------------------------------------

export const CEILINGS = Object.freeze({
  grocery: {
    product: {
      Emax: {
        value: 0.72,
        confidence: 'SRS',
        source: 'VideoMining frozen-aisle engagement rate ~58% of zone-reachers; 0.72 set as a top-decile ceiling above that observed average.',
      },
      Cmax: {
        value: 0.75,
        range: [0.63, 0.83],
        confidence: 'PR',
        source: 'Hui, Huang, Suher & Inman (2013) Journal of Marketing Research 50(4):445-462 — engaged grocery shoppers convert 63-83% (set 0.75). Corroborated by VideoMining (17 of 22 engaged frozen-aisle shoppers buy = 77%).',
      },
    },
    demo: {
      Emax: {
        value: 0.85,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Sampling zones engineered to stop shoppers (free product, sensory cue) → higher engagement than passive aisles.',
      },
      Cmax: {
        value: 0.25,
        range: [0.20, 0.35],
        confidence: 'CVT',
        source: 'Trade data convergence: Starbucks ~20%, Makai 25-35%, Promomash >25%. Three independent commercial sources.',
      },
    },
    display: {
      Emax: {
        value: 0.50,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Endcap shoppers are filtered (drive-by population); engagement lower than product aisle.',
      },
      Cmax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Structurally supported by Inman, Winer & Ferraro (2009) JM 73(5):19-29 display lift coefficient (0.64 unplanned-purchase probability vs 0.46 baseline).',
      },
    },
  },
  diy: {
    product: {
      Emax: {
        value: 0.50,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: project-driven engagement at the target aisle (shoppers come to examine specific items).',
      },
      Cmax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: closure moderate-high — engaged shoppers usually buy the item they came to examine.',
      },
    },
    demo: {
      Emax: {
        value: 0.65,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Active demonstrations attract higher engagement than passive aisles.',
      },
      Cmax: {
        value: 0.15,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Project-planning purchases often delayed; demo conversion lower than aisle.',
      },
    },
    display: {
      Emax: {
        value: 0.35,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Drive-by attention pattern.',
      },
      Cmax: {
        value: 0.30,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Impulse-design endcaps (e.g., consumables alongside power tools); consistent with Inman et al. (2009) display effect.',
      },
    },
  },
  furniture: {
    product: {
      Emax: {
        value: 0.45,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: high examination of those who reach a zone (people who walk onto a sofa floor stop and touch).',
      },
      Cmax: {
        value: 0.35,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: big-ticket / high-deliberation → lower closure (engage-but-defer common).',
      },
    },
    demo: {
      Emax: {
        value: 0.60,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Try-out zones (TV walls, sofa floors, garden machinery demos) attract high engagement.',
      },
      Cmax: {
        value: 0.12,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Very high deferred conversion; high-AOV deliberative purchases rarely impulse.',
      },
    },
    display: {
      Emax: {
        value: 0.30,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Endcap effect proportionally smaller in considered-purchase categories.',
      },
      Cmax: {
        value: 0.22,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Endcap effect proportionally smaller in considered-purchase categories.',
      },
    },
  },
  fashion: {
    product: {
      Emax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'SRS: high tactile browsing of those who reach a zone, but much pass-by in apparel formats.',
      },
      Cmax: {
        value: 0.45,
        confidence: 'CVT',
        source: 'Anchored to the in-store try-on datapoint (fitting-room conversion ~67%); handle→buy set below it (browse-heavy zones).',
      },
    },
    demo: {
      Emax: {
        value: 0.70,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Styling areas (lookbooks, mannequin sets, styling consults) attract attention.',
      },
      Cmax: {
        value: 0.20,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Conversion cycles to fitting-room rather than direct demo conversion.',
      },
    },
    display: {
      Emax: {
        value: 0.40,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Window/display effect well documented in visual-merchandising literature.',
      },
      Cmax: {
        value: 0.32,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Impulse-driven conversion for shoppers who stop.',
      },
    },
  },
  discount: {
    product: {
      Emax: {
        value: 0.45,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: engagement moderate — broad assortment, less mission-focused than grocery.',
      },
      Cmax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: grab-and-go, low-deliberation trips → high closure of engaged shoppers.',
      },
    },
    demo: {
      Emax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Demo zones uncommon in discount formats; values reflect the few cases.',
      },
      Cmax: {
        value: 0.20,
        confidence: 'SRS',
        source: 'Storalytic synthesis.',
      },
    },
    display: {
      Emax: {
        value: 0.35,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Endcap-heavy merchandising model.',
      },
      Cmax: {
        value: 0.32,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Conversion lift consistent with the broader endcap pattern.',
      },
    },
  },
  pharmacy: {
    product: {
      Emax: {
        value: 0.55,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: mission-driven, narrow-assortment trips → engagement moderate-high at the target shelf. Reasoned relative to the grocery engagement anchor.',
      },
      Cmax: {
        value: 0.65,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: mission-driven, narrow-assortment trips → high closure of engaged shoppers (familiar/needed items close high per the consideration-level principle).',
      },
    },
    demo: {
      Emax: {
        value: 0.75,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Beauty counters and skincare try-out zones attract high engagement.',
      },
      Cmax: {
        value: 0.22,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Conversion often deferred or routed through staff consultation.',
      },
    },
    display: {
      Emax: {
        value: 0.40,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Drive-by attention pattern.',
      },
      Cmax: {
        value: 0.40,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Endcap conversion higher; pharmacy displays often anchor mission categories (cold/flu seasonal, allergy) where impulse acceptance is high among already-decided shoppers.',
      },
    },
  },
  specialty: {
    product: {
      Emax: {
        value: 0.65,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: high-intent, premeditated trips (opticians, jewelry) → most who reach a zone engage.',
      },
      Cmax: {
        value: 0.50,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: closure mid for specialty zones (opticians close high, jewelry deliberative/lower).',
      },
    },
    demo: {
      Emax: {
        value: 0.80,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Specialty \'demo\' (trying glasses, jewelry, watches) is often the core selling moment.',
      },
      Cmax: {
        value: 0.35,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Both engagement and conversion high in specialty try-on contexts.',
      },
    },
    display: {
      Emax: {
        value: 0.50,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Filtered-attention zones — those who stop are pre-qualified.',
      },
      Cmax: {
        value: 0.45,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Showcase displays in opticians/jewelry function as filtered-attention zones.',
      },
    },
  },
  large_format: {
    product: {
      Emax: {
        value: 0.48,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: mixed missions; engagement moderate. Forced-path layouts (IKEA-style) raise traffic separately (Sorensen 2017), not zone engagement.',
      },
      Cmax: {
        value: 0.45,
        confidence: 'SRS',
        source: 'No published zone benchmark. SRS: closure moderate for big-box mixed missions.',
      },
    },
    demo: {
      Emax: {
        value: 0.65,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Department-level demo zones (sporting goods try-outs, garden-machinery start-ups).',
      },
      Cmax: {
        value: 0.18,
        confidence: 'SRS',
        source: 'Storalytic synthesis.',
      },
    },
    display: {
      Emax: {
        value: 0.35,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Drive-by attention pattern.',
      },
      Cmax: {
        value: 0.30,
        confidence: 'SRS',
        source: 'Storalytic synthesis. Big-box endcap effect consistent with Inman et al. (2009) coefficient applied to high-traffic format.',
      },
    },
  },
})

// ---------------------------------------------------------------------------
// TWT — Target Wait Time per sector (Checkout Waiting zones)
// ---------------------------------------------------------------------------

export const TWT_BY_SECTOR = Object.freeze({
  grocery: {
    value: 4.0,
    range: [3.0, 5.0],
    unit: 'min',
    confidence: 'PR',
    source: 'Bain NPS Prism (checkout NPS into single digits beyond 5 min); Vision Critical/EE 2014 UK survey of 2,000 consumers (73% abandon >5 min; 59% won\'t wait >4 min).',
  },
  diy: {
    value: 5.5,
    range: [4.0, 7.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Anchored against general-retail walk-out average ~9 min (Box Technologies/Intel UK survey) and complexity principle (Bain/Carmon).',
  },
  furniture: {
    value: 6.0,
    range: [5.0, 8.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Complexity principle (Bain/Carmon) — high-value purchase categories show measurably higher wait tolerance.',
  },
  fashion: {
    value: 5.0,
    range: [4.0, 6.0],
    unit: 'min',
    confidence: 'CVT',
    source: 'General-retail abandonment threshold ~5 min (Vision Critical/EE, QueueAway); apparel-specific MishiPay datapoint corroborates the 4-min checkout interaction pattern.',
  },
  discount: {
    value: 4.0,
    range: [3.0, 5.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Speed-driven, price-sensitive trip pattern → low tolerance, parallel to grocery.',
  },
  pharmacy: {
    value: 5.0,
    range: [4.0, 7.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Front-of-store fast (grocery-parallel) with Rx-pickup tolerance extending upper bound; consistent with US pharmacy queue data ~8.6 min (QueueAway 2026).',
  },
  specialty: {
    value: 8.0,
    range: [6.0, 10.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Complexity principle (Bain/Carmon) at maximum — high-consideration, high-ticket purchases tolerate longest waits.',
  },
  large_format: {
    value: 6.0,
    range: [5.0, 8.0],
    unit: 'min',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Large baskets / journey-length context; corroborated by general-retail walk-out average ~9 min.',
  },
})

// ---------------------------------------------------------------------------
// TIT — Target Interaction Time per sector (Checkout Interaction zones)
// ---------------------------------------------------------------------------

export const TIT_BY_SECTOR = Object.freeze({
  grocery: {
    value: 90,
    range: [60, 120],
    unit: 'sec',
    confidence: 'CVT',
    source: 'Cashier-productivity data ~16 items/min scan rate + ~50 sec payment (consistent across industry checkout-scheduling sources); median grocery basket ≤5 items (Hui et al. 2013).',
  },
  diy: {
    value: 90,
    range: [60, 120],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Triangulated from cashier productivity; mixed basket sizes.',
  },
  furniture: {
    value: 120,
    range: [90, 180],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Fewer items per transaction but higher value (warranty, financing, payment paperwork, bagging of bulky goods).',
  },
  fashion: {
    value: 60,
    range: [45, 90],
    unit: 'sec',
    confidence: 'CVT',
    source: 'MishiPay datapoint: ~90 sec traditional apparel checkout; ~20 sec with RFID self-checkout. Range reflects mixed deployment.',
  },
  discount: {
    value: 60,
    range: [45, 90],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Fast-scan model (Aldi-style); cashier-productivity-dominated.',
  },
  pharmacy: {
    value: 90,
    range: [60, 150],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Front-of-store fast; Rx counter adds prescription verification time.',
  },
  specialty: {
    value: 180,
    range: [120, 300],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Consultative sale embedded in checkout; gift-wrap, packaging, payment paperwork standard.',
  },
  large_format: {
    value: 120,
    range: [90, 180],
    unit: 'sec',
    confidence: 'SRS',
    source: 'Storalytic synthesis. Large baskets; cashier scan rate ~16 items/min dominates.',
  },
})

// ---------------------------------------------------------------------------
// Helpers — Level 1 / Level 2 lookups
// ---------------------------------------------------------------------------

function resolveSector(sector) {
  if (typeof sector === 'string' && sector in CEILINGS) {
    return sector
  }
  return DEFAULT_SECTOR
}

/**
 * Level 1 lookup: returns the sector's default {Emax, Cmax} pair as raw
 * numbers, derived from the 'product' zone-type slot for the given sector.
 *
 * Use when the consumer doesn't care about zone type (e.g. a store-wide
 * aggregate display).
 *
 * @param {string} sector
 * @returns {{Emax: number, Cmax: number}}
 */
export function getSectorDefault(sector) {
  const s = resolveSector(sector)
  const p = CEILINGS[s].product
  return { Emax: p.Emax.value, Cmax: p.Cmax.value }
}

/**
 * Level 2 lookup: returns the (sector × zoneType) {Emax, Cmax} cells
 * with full provenance metadata (confidence, source, optional range).
 *
 * zoneType is normalised case-insensitively; unrecognised values fall
 * back to 'product' (the safest, most-common slot).
 *
 * @param {string} sector
 * @param {string} zoneType — 'product' | 'demo' | 'display'
 * @returns {{Emax: {value, confidence, source, range?}, Cmax: {...}}}
 */
export function getZoneTypeDefault(sector, zoneType) {
  const s = resolveSector(sector)
  const zt = String(zoneType || '').toLowerCase()
  const safeZt = ZONE_TYPES_WITH_CEILINGS.includes(zt) ? zt : 'product'
  return CEILINGS[s][safeZt]
}

/**
 * Per-zone ceiling resolution — the bridge between this reference module
 * and the legacy classifier API (which expects raw {Emax, Cmax} numbers,
 * not the rich provenance shape returned by getZoneTypeDefault).
 *
 * Resolution order for Emax + Cmax:
 *   1. SectorOverride (in DetectionZoneSettings) sets the EFFECTIVE
 *      SECTOR — "this pharmacy endcap inside our DIY store behaves
 *      like pharmacy." Falls back to companySector, then DEFAULT_SECTOR.
 *   2. Level-2 sector × zone-type default. Single yardstick, always
 *      available, scales per sector.
 *
 * The earlier 3-level design (Level 3 = per-zone P95 weekly engagement
 * as a ceiling, with a "Sector aspirational stretch" line on the gauge)
 * was retired because the calibrated value collapsed to current
 * performance for stable stores, producing zero engagement headroom.
 * The nightly job still writes PastPeakEngagement_p95 (formerly
 * SelfCalibrated_Emax) for use as a DESCRIPTIVE past-peak reference on
 * the hero (see computeTopWeeksContext in storeEfficiency.js), but it
 * no longer drives any ceiling math.
 *
 * The `zoneOverride` argument is overloaded for backward compatibility:
 *   - string  → SectorOverride key (legacy call shape)
 *   - object  → { sectorOverride, pastPeakEngagementP95, ... }
 *   - null/undefined → no override at all
 *
 * @param {string|null|undefined} companySector — the company's sector
 *        (from CompanySettings.sector). Falls back to DEFAULT_SECTOR.
 * @param {string|null|undefined|{sectorOverride?: string, pastPeakEngagementP95?: number}} zoneOverride
 * @param {string} zoneType — 'product' | 'demo' | 'display'.
 * @returns {{Emax: number, Cmax: number}} raw numbers compatible with
 *          the legacy CEILINGS shape consumed by classifier.js.
 */
export function resolveZoneCeiling(companySector, zoneOverride, zoneType = 'product') {
  // Industry sector ceiling is the ONLY ceiling driving the math.
  // Honest reference, always knowable, doesn't require enough history.
  //
  // PastPeakEngagement_p95 (the 95th-percentile-of-weekly value written
  // by zone_calibration.py) is no longer consulted here — it was an
  // attempt to give stores a softer "self-paced" ceiling, but it
  // collapsed to current performance for stable stores, breaking the
  // engagement-lever maths (engagement headroom dropped to zero even
  // when the zone was nowhere near its industry potential). Past
  // performance is still useful as descriptive CONTEXT (best 30-day
  // stretch, trend) but should not be a ceiling.
  //
  // SectorOverride per zone IS still respected — admin can declare
  // "treat this zone like sector X" (e.g. a pharmacy endcap inside a
  // DIY store).
  const sectorOverride =
    typeof zoneOverride === 'string'
      ? zoneOverride
      : (zoneOverride && zoneOverride.sectorOverride) || null

  const effectiveSector = sectorOverride || companySector
  const cell = getZoneTypeDefault(effectiveSector, zoneType)

  return {
    Emax: cell.Emax.value,
    Cmax: cell.Cmax.value,
  }
}

/**
 * Returns the {value, range, confidence, source, unit} target wait time
 * for the sector. Unit is always 'min'.
 *
 * @param {string} sector
 */
export function getTwt(sector) {
  return TWT_BY_SECTOR[resolveSector(sector)]
}

/**
 * Returns the {value, range, confidence, source, unit} target interaction
 * time for the sector. Unit is always 'sec'.
 *
 * @param {string} sector
 */
export function getTit(sector) {
  return TIT_BY_SECTOR[resolveSector(sector)]
}
